import { createSelector, Selector } from '@ngxs/store';
import { CgvState, CgvStateModel } from './cgv.state';
import {
  theTextsToPutToTheMember,
  ATextPutAgainToTheMember,
} from '../domain/a-new-version-of-the-terms-or-the-policy-is-put-to-the-member';
import { ALegalTextAMemberCanRead } from '../domain/the-legal-texts-a-member-can-read';
export class CgvSelectors {
  @Selector([CgvState])
  static data(state: CgvStateModel) {
    return state.list;
  }

  @Selector([CgvState])
  static cgvs(state: CgvStateModel) {
    return state.list?.cgvs ?? [];
  }

  @Selector([CgvState])
  static loading(state: CgvStateModel) {
    return state.loading;
  }

  @Selector([CgvState])
  static error(state: CgvStateModel) {
    return state.error;
  }

  @Selector([CgvState])
  static lastVersion(state: CgvStateModel) {
    return state.lastVersion;
  }
  @Selector([CgvState])
  static firstCgv(state: CgvStateModel) {
    return state.list?.cgvs?.[0] ?? null;
  }
  static lastVersionOfFirstCgv() {
    return createSelector([CgvSelectors.firstCgv, CgvState], (cgv, state: CgvStateModel) => {
      if (!cgv?._id) return null;

      /*
       * A HALF-FINISHED REFACTOR, AND THIS IS THE HALF THAT WAS MISSING.
       *
       * This selector indexed `state.lastVersion` by a CGV id, as if the state
       * held a map. It does not: `loadLastVersionSuccess` writes ONE version
       * into that field, overwriting it. So the lookup asked a version object
       * for a property named after an id, always got undefined, and this
       * selector answered null every single time -- which is why the legal
       * text screen waits for its timeout and then shows its failure state.
       *
       * The map exists a few lines below the writer, COMMENTED OUT, keyed by
       * `version.cgv._id`. Somebody started it and stopped. Rather than turn
       * that on, which changes what the state holds, this asks the version
       * which CGV it belongs to -- it carries `cgvId` -- which is what the
       * lookup was reaching for.
       *
       * It answers for ONE CGV at a time, the last one loaded, because the
       * writer still overwrites. The legal text screen asks for `limit: 1`, so
       * that is its whole world. Turning the state into a real map is its own
       * piece of work, and it belongs with whoever owns those screens.
       */
      const last = state.lastVersion;

      return last?.cgvId === cgv._id ? last : null;
    });
  }
  @Selector([CgvState])
  static checkNewVersion(state: CgvStateModel) {
    return state.newVersion;
  }

  @Selector([CgvState])
  static checkNewCommissionVersion(state: CgvStateModel) {
    return state.newCommissionVersion ?? state.newVersion;
  }

  @Selector([CgvState])
  static checkNewGcsVersion(state: CgvStateModel) {
    return state.newGcsVersion ?? false;
  }

  /** The terms of use and the privacy policy to put to the member, in order. */
  @Selector([CgvState])
  static textsToPutToTheMember(state: CgvStateModel): ATextPutAgainToTheMember[] {
    return theTextsToPutToTheMember({ cgu: state.newCguVersion, privacy: state.newPrivacyVersion });
  }

  /**
   * The last version loaded, only once it is the one of this text.
   *
   * The state holds ONE list and ONE version, overwritten by every load. The
   * window for the terms opens right after the one for the sale conditions, so
   * until its own load answers, the state still holds the sale conditions, and
   * `lastVersionOfFirstCgv` would hand them over: the member would be asked to
   * accept the terms of use under the text of the sale conditions. Checking
   * the type of the listed cgv closes that.
   *
   * Recette 2.2.11: the Legal information screen had the same hole. It read
   * `lastVersionOfFirstCgv`, so opening the terms of use and then the privacy
   * policy showed the privacy title over the body of the terms, still in the
   * state from the first visit. It reads this selector now, which is why the
   * parameter takes every text a member can open and not only the two put
   * again to them.
   */
  static lastVersionOf(text: ALegalTextAMemberCanRead | ATextPutAgainToTheMember) {
    return createSelector([CgvSelectors.firstCgv, CgvSelectors.lastVersionOfFirstCgv()], (cgv, version) =>
      cgv?.type === text ? version : null,
    );
  }
}
